import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Maximize2 } from "lucide-react";
import { staffApi } from "@client/api/staff";
import { StaffShell } from "@client/components/StaffShell";
import { Badge } from "@client/components/ui/badge";
import { useConfirm } from "@client/components/confirm-dialog";
import {
  sessionStatusLabel,
  sessionStatusBadgeClass,
} from "@client/lib/session-status";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@client/components/ui/select";

export const Route = createFileRoute("/_authenticated/sessions/")({
  component: SessionsPage,
});

function SessionsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [examId, setExamId] = useState("");
  const [title, setTitle] = useState("");

  const { data } = useQuery({
    queryKey: ["sessions"],
    queryFn: staffApi.sessions,
  });
  const exams = data?.exams;
  const sessions = data?.sessions;

  const create = useMutation({
    mutationFn: async () =>
      staffApi.createSession({ examId, title: title.trim() }),
    onSuccess: (res) => {
      toast.success("Session created");
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      navigate({
        to: "/sessions/$sessionId",
        params: { sessionId: res.sessionId },
      });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteSessionMut = useMutation({
    mutationFn: (id: string) => staffApi.deleteSession(id),
    onSuccess: () => {
      toast.success("Session deleted successfully");
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const clearAllMut = useMutation({
    mutationFn: () => staffApi.clearAllSessions(),
    onSuccess: () => {
      toast.success("All sessions cleared");
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <StaffShell
      title="Sessions"
      subtitle="Hall sessions with QR entry and synchronised start"
    >
      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <form
          className="panel space-y-4 p-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!examId) {
              toast.error("Choose an exam");
              return;
            }
            if (title.trim().length < 3) {
              toast.error("Enter a session title");
              return;
            }
            create.mutate();
          }}
        >
          <h2 className="hall-heading text-lg font-bold">New session</h2>
          <div className="space-y-2">
            <Label>Exam</Label>
            <Select value={examId} onValueChange={setExamId}>
              <SelectTrigger>
                <SelectValue placeholder="Select exam" />
              </SelectTrigger>
              <SelectContent>
                {(exams ?? []).map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="title">Session title</Label>
            <Input
              id="title"
              placeholder="e.g. National Referee Exam — Hall A"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <Button type="submit" className="w-full" disabled={create.isPending}>
            Create session
          </Button>
        </form>

        <div className="panel p-5">
          <div className="flex items-center justify-between">
            <h2 className="hall-heading text-lg font-bold">All sessions</h2>
            {sessions?.length ? (
              <Button
                variant="destructive"
                size="sm"
                className="h-8 gap-1 text-xs"
                onClick={async () => {
                  if (
                    await confirm({
                      title: "Delete all sessions?",
                      description:
                        "This will also remove associated referee attempts. This action cannot be undone.",
                      confirmText: "Delete all",
                      destructive: true,
                    })
                  ) {
                    clearAllMut.mutate();
                  }
                }}
                disabled={clearAllMut.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Clear All
              </Button>
            ) : null}
          </div>
          <div className="mt-4 space-y-2">
            {(sessions ?? []).map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between rounded-md border border-border px-4 py-3 hover:bg-secondary/50 group"
              >
                <Link
                  to="/sessions/$sessionId"
                  params={{ sessionId: s.id }}
                  className="flex-1 min-w-0 pr-4"
                >
                  <p className="font-semibold text-foreground truncate">
                    {s.title}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {(s.exams as { name: string } | null)?.name}
                  </p>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge
                    variant="outline"
                    className={sessionStatusBadgeClass(s.status)}
                  >
                    {sessionStatusLabel(s.status)}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1 text-xs text-primary hover:bg-primary/10"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate({
                        to: "/sessions/$sessionId",
                        params: { sessionId: s.id },
                        search: { present: "true" } as any,
                      });
                    }}
                    title="Display QR Code & Link"
                  >
                    <Maximize2 className="h-3.5 w-3.5" />
                    Display QR
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (
                        await confirm({
                          title: "Delete session?",
                          description: `Delete session "${s.title}"? This will also remove associated referee attempts.`,
                          confirmText: "Delete",
                          destructive: true,
                        })
                      ) {
                        deleteSessionMut.mutate(s.id);
                      }
                    }}
                    disabled={deleteSessionMut.isPending}
                    title="Delete session"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {!sessions?.length ? (
              <p className="text-sm text-muted-foreground">No sessions yet.</p>
            ) : null}
          </div>
        </div>
      </div>
    </StaffShell>
  );
}
